import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';

const PostDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentContent, setCommentContent] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [editingPost, setEditingPost] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    if (id == null || id === '') {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get(`/posts/${id}`);
      setPost(response.data);
    } catch (err) {
      console.error('Failed to load post:', err);
      // Keep post null so we show "Post not found" for 404 or other errors
    } finally {
      setLoading(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) {
      alert('Please login to comment');
      return;
    }

    try {
      await api.post(`/posts/${id}/comments`, { content: commentContent });
      setCommentContent('');
      fetchPost();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add comment');
    }
  };

  const handleEditComment = async (commentId) => {
    try {
      await api.put(`/posts/${id}/comments/${commentId}`, { content: editCommentContent });
      setEditingComment(null);
      setEditCommentContent('');
      fetchPost();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await api.delete(`/posts/${id}/comments/${commentId}`);
      fetchPost();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete comment');
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await api.delete(`/posts/${id}`);
      navigate('/posts');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete post');
    }
  };

  const startEditPost = () => {
    setEditTitle(post.title);
    setEditContent(post.content);
    setEditImageUrl(post.image_url || '');
    setEditingPost(true);
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append('image', file);
    const res = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data.url;
  };

  const handleEditImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setEditImageUrl(url);
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/posts/${id}`, { title: editTitle, content: editContent, image_url: editImageUrl });
      setPost(prev => ({ ...prev, title: editTitle, content: editContent, image_url: editImageUrl }));
      setEditingPost(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update post');
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleString(undefined, { timeZone: 'America/Los_Angeles', dateStyle: 'short', timeStyle: 'short' });

  if (loading) {
    return <div>Loading post...</div>;
  }

  if (!post) {
    return <div>Post not found</div>;
  }

  const canEditPost = user && post.user_id === user.id;

  return (
    <div>
      <Link to="/posts" className="btn btn-secondary" style={{ marginBottom: '20px', textDecoration: 'none', display: 'inline-block' }}>
        ← Back to Posts
      </Link>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            {!editingPost ? (
              <>
                <h2>{post.title}</h2>
                <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
                  By {post.author_name} • {formatDate(post.created_at)}
                </div>
                {post.image_url && (
                  <img
                    src={post.image_url.startsWith('/') ? window.location.origin + post.image_url : post.image_url}
                    alt=""
                    style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', marginBottom: 15 }}
                  />
                )}
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                  {post.content}
                </div>
              </>
            ) : (
              <form onSubmit={handleSavePost}>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="form-group">
                  <label>Content *</label>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="form-group">
                  <label>Image (optional)</label>
                  <div style={{ marginBottom: 8 }}>
                    <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleEditImageFile} disabled={uploading} />
                    {uploading && <span style={{ marginLeft: 8, color: '#666' }}>Uploading…</span>}
                  </div>
                  <input
                    type="text"
                    placeholder="Or paste image URL"
                    value={editImageUrl}
                    onChange={(e) => setEditImageUrl(e.target.value)}
                    style={{ width: '100%' }}
                  />
                  {editImageUrl && (
                    <div style={{ marginTop: 8 }}>
                      <img src={editImageUrl.startsWith('/') ? window.location.origin + editImageUrl : editImageUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} />
                    </div>
                  )}
                </div>
                <button type="submit" className="btn btn-primary">Save</button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditingPost(false)} style={{ marginLeft: 8 }}>Cancel</button>
              </form>
            )}
          </div>
          {canEditPost && !editingPost && (
            <div>
              <button className="btn btn-secondary btn-sm" onClick={startEditPost} style={{ marginRight: 8 }}>Edit</button>
              <button className="btn btn-danger btn-sm" onClick={handleDeletePost}>
                Delete Post
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <h3>Comments ({post.comments?.length || 0})</h3>

        {user ? (
          <form onSubmit={handleComment} style={{ marginBottom: '20px' }}>
            <div className="form-group">
              <label>Add a comment</label>
              <textarea
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                required
                rows="3"
              />
            </div>
            <button type="submit" className="btn btn-primary">Post Comment</button>
          </form>
        ) : (
          <p style={{ marginBottom: '20px', color: '#666' }}>Please login to comment</p>
        )}

        {post.comments && post.comments.length > 0 ? (
          post.comments.map((comment) => (
            <div key={comment.id} style={{ padding: '15px', borderBottom: '1px solid #eee', marginBottom: '10px' }}>
              {editingComment === comment.id ? (
                <div>
                  <textarea
                    value={editCommentContent}
                    onChange={(e) => setEditCommentContent(e.target.value)}
                    rows="3"
                    style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
                  />
                  <div>
                    <button className="btn btn-primary btn-sm" onClick={() => handleEditComment(comment.id)}>
                      Save
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditingComment(null); setEditCommentContent(''); }} style={{ marginLeft: '10px' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ whiteSpace: 'pre-wrap', marginBottom: '10px' }}>{comment.content}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#666' }}>
                    <span>By {comment.author_name} • {formatDate(comment.created_at)}</span>
                    {user && user.id === comment.user_id && (
                      <div>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditingComment(comment.id); setEditCommentContent(comment.content); }}>
                          Edit
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteComment(comment.id)} style={{ marginLeft: '5px' }}>
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))
        ) : (
          <p style={{ color: '#666', fontStyle: 'italic' }}>No comments yet. Be the first to comment!</p>
        )}
      </div>
    </div>
  );
};

export default PostDetail;

